import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getUsers, createUser, updateUser, deleteUser, resetUserPassword, getAutomationLogs } from '../api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  RefreshCw, 
  Edit2, 
  X, 
  Check, 
  Users, 
  History,
  UserPlus,
  Trash2,
  KeyRound
} from 'lucide-react';

const Admin = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      if (activeTab === 'users') {
        const response = await getUsers();
        setUsers(response.data);
      } else {
        const response = await getAutomationLogs(200);
        setLogs(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleActive = async (userId, isActive) => {
    try {
      await updateUser(userId, { is_active: !isActive });
      toast.success(isActive ? 'User deactivated' : 'User activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (targetUser) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${targetUser.name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await deleteUser(targetUser.id);
      toast.success(`User "${targetUser.name}" deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      l1_tech: 'bg-blue-500/10 text-blue-400 border-blue-500/50',
      l2_tech: 'bg-purple-500/10 text-purple-400 border-purple-500/50',
      inbound_sales: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/50',
      l3_tech: 'bg-orange-500/10 text-orange-400 border-orange-500/50',
      manager: 'bg-red-500/10 text-red-400 border-red-500/50',
    };
    return styles[role] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/50';
  };

  const formatRole = (role) => {
    const labels = {
      l1_tech: 'L1 Tech',
      l2_tech: 'L2 Tech',
      inbound_sales: 'Inbound Sales',
      l3_tech: 'L3 Tech',
      manager: 'Manager',
    };
    return labels[role] || role;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-text-primary">Admin Settings</h1>
          <p className="text-text-secondary text-sm mt-1">User management and system logs</p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 rounded-md bg-surface border border-border text-text-secondary hover:text-text-primary hover:bg-surface-highlight transition-all"
          data-testid="refresh-admin"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-surface p-1 rounded-lg border border-border w-fit" data-testid="admin-tabs">
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'users'
              ? 'bg-primary text-primary-foreground'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-highlight'
          }`}
          data-testid="tab-users"
        >
          <Users size={16} />
          Users
        </button>
        <button
          onClick={() => setActiveTab('automation')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'automation'
              ? 'bg-primary text-primary-foreground'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-highlight'
          }`}
          data-testid="tab-automation"
        >
          <History size={16} />
          Automation Log
        </button>
      </div>

      {/* Content */}
      <div className="bg-surface border border-border rounded-lg">
        {loading && (
          <div className="p-8 text-center">
            <div className="flex items-center justify-center gap-2 text-text-secondary">
              <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full spinner" />
              Loading...
            </div>
          </div>
        )}
        {!loading && activeTab === 'users' && (
          <>
            <div className="p-4 border-b border-border flex items-center justify-between">
              <span className="text-sm text-text-secondary">{users.length} users</span>
              <button
                onClick={() => { setEditingUser(null); setShowUserModal(true); }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                data-testid="add-user-btn"
              >
                <UserPlus size={16} />
                Add User
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead>
                  <tr className="border-b border-border bg-surface-highlight">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Level</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-surface-highlight/50">
                      <td className="px-4 py-3 text-text-primary font-medium">{u.name}</td>
                      <td className="px-4 py-3 text-text-secondary">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${getRoleBadge(u.role)}`}>
                          {formatRole(u.role)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs ${u.role_level === 'lead' ? 'text-warning' : 'text-text-secondary'}`}>
                          {u.role_level === 'lead' ? 'Lead' : 'Member'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          u.is_active 
                            ? 'bg-success/10 text-success border border-success/50' 
                            : 'bg-destructive/10 text-destructive border border-destructive/50'
                        }`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button
                            onClick={() => { setEditingUser(u); setShowUserModal(true); }}
                            className="p-1.5 text-text-secondary hover:text-primary hover:bg-primary/10 rounded transition-colors"
                            title="Edit user"
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => { setResetPasswordUser(u); setShowResetPasswordModal(true); }}
                            className="p-1.5 text-text-secondary hover:text-warning hover:bg-warning/10 rounded transition-colors"
                            title="Reset password"
                            data-testid={`reset-pw-${u.id}`}
                          >
                            <KeyRound size={16} />
                          </button>
                          <button
                            onClick={() => handleToggleActive(u.id, u.is_active)}
                            className={`p-1.5 rounded transition-colors ${
                              u.is_active 
                                ? 'text-text-secondary hover:text-destructive hover:bg-destructive/10' 
                                : 'text-text-secondary hover:text-success hover:bg-success/10'
                            }`}
                            title={u.is_active ? 'Deactivate user' : 'Activate user'}
                            data-testid={`toggle-user-${u.id}`}
                          >
                            {u.is_active ? <X size={16} /> : <Check size={16} />}
                          </button>
                          {u.id !== user?.id && (
                            <button
                              onClick={() => handleDeleteUser(u)}
                              className="p-1.5 text-text-secondary hover:text-destructive hover:bg-destructive/10 rounded transition-colors"
                              title="Delete user"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {!loading && activeTab === 'automation' && (
          <div className="p-4">
            <h3 className="text-lg font-heading font-semibold text-text-primary mb-4">Automation Log</h3>
            {logs.length === 0 ? (
              <p className="text-text-secondary text-center py-8">No automation events logged</p>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {logs.map((log) => (
                  <div key={log.id} className="p-3 bg-background rounded-md border border-border" data-testid={`log-${log.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded font-mono">
                            {log.rule_id}
                          </span>
                          <span className="text-sm font-medium text-text-primary">{log.action}</span>
                        </div>
                        {log.details && (
                          <p className="text-sm text-text-secondary">{log.details}</p>
                        )}
                        {log.account_id !== 'batch' && (
                          <p className="text-xs text-text-secondary mt-1">Account: {log.account_id}</p>
                        )}
                      </div>
                      <span className="text-xs text-text-secondary whitespace-nowrap">
                        {format(new Date(log.timestamp), 'MMM d, h:mm:ss a')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <UserModal
          user={editingUser}
          onClose={() => { setShowUserModal(false); setEditingUser(null); }}
          onSuccess={() => { setShowUserModal(false); setEditingUser(null); fetchData(); }}
        />
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordUser && (
        <ResetPasswordModal
          targetUser={resetPasswordUser}
          onClose={() => { setShowResetPasswordModal(false); setResetPasswordUser(null); }}
          onSuccess={() => { setShowResetPasswordModal(false); setResetPasswordUser(null); }}
        />
      )}
    </div>
  );
};

const UserModal = ({ user: editingUser, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: editingUser?.email || '',
    password: '',
    name: editingUser?.name || '',
    role: editingUser?.role || 'l1_tech',
    role_level: editingUser?.role_level || 'member'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          role_level: formData.role_level
        };
        await updateUser(editingUser.id, updateData);
        toast.success('User updated');
      } else {
        if (!formData.password) {
          toast.error('Password is required');
          setLoading(false);
          return;
        }
        await createUser(formData);
        toast.success('User created');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-surface border border-border rounded-lg shadow-xl" data-testid="user-modal">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-text-primary">
            {editingUser ? 'Edit User' : 'Add User'}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-highlight text-text-secondary">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="user-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="user-email-input"
            />
          </div>
          {!editingUser && (
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="user-password-input"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="user-role-select"
            >
              <option value="l1_tech">L1 Tech</option>
              <option value="l2_tech">L2 Tech</option>
              <option value="inbound_sales">Inbound Sales</option>
              <option value="l3_tech">L3 Tech</option>
              <option value="manager">Manager</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Role Level</label>
            <select
              value={formData.role_level}
              onChange={(e) => setFormData({ ...formData, role_level: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="user-level-select"
            >
              <option value="member">Member</option>
              <option value="lead">Lead</option>
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-surface-highlight text-text-primary rounded-md font-medium hover:bg-border transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              data-testid="save-user-btn"
            >
              {loading ? 'Saving...' : editingUser ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ResetPasswordModal = ({ targetUser, onClose, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await resetUserPassword(targetUser.id, newPassword);
      toast.success(`Password reset for ${targetUser.name}`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-surface border border-border rounded-lg shadow-xl" data-testid="reset-password-modal">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-text-primary">Reset Password</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-highlight text-text-secondary">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <p className="text-sm text-text-secondary">
            Set a new password for <span className="text-text-primary font-medium">{targetUser.name}</span> ({targetUser.email})
          </p>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              placeholder="Minimum 6 characters"
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="new-password-input"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-surface-highlight text-text-primary rounded-md font-medium hover:bg-border transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-warning text-white rounded-md font-medium hover:bg-warning/90 transition-colors disabled:opacity-50"
              data-testid="confirm-reset-pw-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Admin;
